import { IconDotsVertical } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import {
    DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
} from 'reactstrap';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthContext } from 'contexts/AuthContext';
import { IProForma } from 'queries/ProForma/useGetProFormasByDeal';
import RenderIf from 'components/RenderIf/RenderIf';
import { formatUTCtoLocal } from 'ns_libs/formatter';
import { NSBadge, NSCard, NSModal } from 'bricks';
import { URLS } from 'services/urls';
import EmptyState from 'components/EmptyState/EmptyState';
import { useDuplicateProForma, useHardDeleteProForma, useUpdateProForma } from 'queries/ProForma';
import { getServerErrorDetail } from 'queries/ProForma/useUpdateProFormaSubprojectLink';
import UserDisplay from 'components/UserDisplay/UserDisplay';
import { resolveUserDisplayName } from 'components/UserDisplay/helpers';
import { useOrganizationUsersContext } from 'contexts/OrganizationUsersContext';
import SortingIcon from 'components/DraggableTable/DraggableHeader/SortingIcon/SortingIcon';
import LinkToProjectModal from 'components/LinkToProjectModal/LinkToProjectModal';
import UnlinkProjectModal from 'components/UnlinkProjectModal/UnlinkProjectModal';
import useToast from 'hooks/useToast';
import { formatProFormaAssetTypeName } from 'helpers/proForma';
import NSTable from '../../../../bricks/NSTable/NSTable';
import {
    getSortingIconProps,
    sortItems,
    SortConfig,
} from '../../helpers';
import {
    headerCellClassName,
    DELETE_LINKED_PRO_FORMA_CONFIRMATION_TEXT,
    DELETE_PRO_FORMA_CONFIRMATION_TEXT,
    LINKED_TO_PROJECT_BADGE_TEXT,
    PRO_FORMA_DELETE,
    PRO_FORMA_DUPLICATE,
    PRO_FORMA_ELLIPSIS_OPTIONS,
    PRO_FORMA_LINK_TO_PROJECT,
    PRO_FORMA_STATUS,
    PRO_FORMA_STATUS_MAPPING,
    PRO_FORMA_SET_AS_ACTIVE,
    PRO_FORMA_UNLINK_FROM_PROJECT,
    PRO_FORMAS_TABLE_HEADERS,
} from '../../constants';

export interface IDealAutomatedTableProps {
    proFormas: IProForma[];
    isLoading?: boolean;
    dealId?: number;
    isAllDealsProFormasPage?: boolean;
}

const DealAutomatedTable = ({
    proFormas,
    isLoading,
    dealId: dealIdProp,
    isAllDealsProFormasPage = false,
}: IDealAutomatedTableProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { selectedOrganizationId } = useAuthContext();
    const { dealId: dealIdFromParams } = useParams<{ dealId: string }>();
    const dealId = dealIdProp || Number(dealIdFromParams);

    const draftOrInProgressStatus = new Set([PRO_FORMA_STATUS.DRAFT, PRO_FORMA_STATUS.IN_PROGRESS]);

    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [proFormaToDelete, setProFormaToDelete] = useState<{ id: number; proFormaStatusId: number; isLinkedToProject: boolean } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [proFormaToLink, setProFormaToLink] = useState<IProForma | null>(null);
    const [proFormaToUnlink, setProFormaToUnlink] = useState<IProForma | null>(null);
    const [sortedBy, setSortedBy] = useState<SortConfig | null>(null);
    const { showError } = useToast();

    const { getUser, isLoading: isUsersLoading } = useOrganizationUsersContext();

    // Sort the Created-by column by what's shown. On the all-deals page createdBy/updatedBy
    // already hold backend-resolved names (signups endpoint); per-deal they are correlation ids.
    const proFormasWithNames = React.useMemo(
        () => (proFormas || []).map(proForma => {
            let createdByName = 'n/a';
            if (proForma.createdBy) {
                createdByName = isAllDealsProFormasPage
                    ? proForma.createdBy
                    : resolveUserDisplayName(getUser(proForma.createdBy), isUsersLoading);
            }
            return { ...proForma, createdByName };
        }),
        [proFormas, getUser, isUsersLoading, isAllDealsProFormasPage],
    );

    const sortedProFormas = React.useMemo(() => sortItems(proFormasWithNames, sortedBy, sortedBy?.key || ''), [proFormasWithNames, sortedBy]);

    const { mutate: updateProForma } = useUpdateProForma();
    const handleUpdateProForma = (proFormaId: number, patchData: object) => {
        updateProForma(
            { dealId: Number(dealId), proFormaId, patchData },
            {
                onSuccess: () => queryClient.invalidateQueries({ queryKey: ['getProFormas'] }),
            },
        );
    };

    const { mutate: hardDeleteProForma } = useHardDeleteProForma();

    const { mutate: duplicateProForma } = useDuplicateProForma();
    const handleDuplicateProForma = async (proFormaId: number) => duplicateProForma({ organizationId: selectedOrganizationId!, proFormaId });

    const handleEllipsisOption = (option: string, proForma: IProForma) => {
        if (option === PRO_FORMA_DUPLICATE) handleDuplicateProForma(proForma.id);
        if (option === PRO_FORMA_SET_AS_ACTIVE) handleUpdateProForma(proForma.id, { isActive: true });
        if (option === PRO_FORMA_LINK_TO_PROJECT) {
            if (proForma.correlationId == null || proForma.assetTypeCorrelationId == null) {
                showError('Could not open the project picker. Refresh the page and try again.');
            } else {
                setProFormaToLink(proForma);
            }
        }
        if (option === PRO_FORMA_UNLINK_FROM_PROJECT) setProFormaToUnlink(proForma);
        if (option === PRO_FORMA_DELETE) {
            setProFormaToDelete({
                id: proForma.id,
                proFormaStatusId: proForma.proFormaStatusId,
                isLinkedToProject: proForma.subprojectId != null,
            });
            setShowDeleteConfirmationModal(true);
        }
    };

    const handleConfirmDelete = () => {
        if (!proFormaToDelete || isDeleting) return;
        setIsDeleting(true);
        const mutationOptions = {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['getProFormas'] });
                setShowDeleteConfirmationModal(false);
                setProFormaToDelete(null);
            },
            onError: (error: Error) => showError(getServerErrorDetail(error) ?? 'Failed to delete pro forma. Please try again.'),
            onSettled: () => setIsDeleting(false),
        };
        if (draftOrInProgressStatus.has(proFormaToDelete.proFormaStatusId)) {
            hardDeleteProForma({ dealId: Number(dealId), proFormaId: proFormaToDelete.id }, mutationOptions);
        } else {
            updateProForma(
                { dealId: Number(dealId), proFormaId: proFormaToDelete.id, patchData: { deletedAt: new Date().toISOString() } },
                mutationOptions,
            );
        }
    };

    const getProFormaEllipsisOptions = (status: number, isActive: boolean, isLinkedToProject: boolean) => {
        const baseOptions = [...PRO_FORMA_ELLIPSIS_OPTIONS[status]];
        const linkOption = isLinkedToProject ? PRO_FORMA_UNLINK_FROM_PROJECT : PRO_FORMA_LINK_TO_PROJECT;
        const deleteIndex = baseOptions.indexOf(PRO_FORMA_DELETE);
        baseOptions.splice(deleteIndex >= 0 ? deleteIndex : baseOptions.length, 0, linkOption);
        if (!isActive && !draftOrInProgressStatus.has(status)) return [...baseOptions, PRO_FORMA_SET_AS_ACTIVE];
        return baseOptions;
    };

    const redirectToProForma = (proForma: IProForma) => {
        const path = generatePath(URLS.PRO_FORMA.TABLE, { dealId, proFormaId: proForma.id });
        navigate(path);
    };

    const getDropdownHeapClassName = (option: string) => ({
        Duplicate: 'heap-deal-pro-forma-home-duplicate',
        Delete: 'heap-deal-pro-forma-home-delete',
        'Set as active': 'heap-deal-pro-forma-home-set-as-active',
        [PRO_FORMA_LINK_TO_PROJECT]: 'heap-deal-pro-forma-home-link-to-project',
        [PRO_FORMA_UNLINK_FROM_PROJECT]: 'heap-deal-pro-forma-home-unlink-from-project',
    }[option] || '');

    return (
        <>
            <RenderIf isTrue={proFormas?.length === 0} isLoading={isLoading}>
                <EmptyState
                    title="No pro formas created yet!"
                    className={isAllDealsProFormasPage ? 'pt-2 pb-1' : 'vh-100'}
                    titleClassName={isAllDealsProFormasPage ? 'text-muted h6' : undefined}
                />
            </RenderIf>
            <RenderIf isTrue={proFormas?.length !== 0}>
                <NSTable className="NSTable--no-footer">
                    <thead className="NSTable__thead">
                        <tr className="NSTable__thead__tr">
                            {PRO_FORMAS_TABLE_HEADERS.map(header => (
                                <th key={header.key} className="NSTable__thead__tr__th">
                                    <div className={headerCellClassName}>
                                        <span>{header.name}</span>
                                        <SortingIcon {...getSortingIconProps(header.key, sortedBy, setSortedBy)} />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="NSTable__tbody">
                        {sortedProFormas.map(proForma => (
                            <tr key={proForma.id} className="NSTable__tbody__tr text-dark">
                                <td>
                                    <span role="presentation" className="text-primary cursor--pointer" onClick={() => redirectToProForma(proForma)}>
                                        {proForma.name}
                                    </span>
                                    <RenderIf isTrue={proForma.isActive}>
                                        <NSBadge className="ml-2" color="primary-lighten">
                                            Active
                                        </NSBadge>
                                    </RenderIf>
                                    <RenderIf isTrue={proForma.subprojectId != null}>
                                        <NSBadge className="ml-2" color="primary-lighten">
                                            {LINKED_TO_PROJECT_BADGE_TEXT}
                                        </NSBadge>
                                    </RenderIf>
                                </td>
                                <td>
                                    {proForma.proFormaModelId && proForma.proFormaModelName
                                        ? formatProFormaAssetTypeName(proForma.proFormaModelName)
                                        : 'n/a'}
                                </td>
                                <td>{proForma.boeScenarioId ? proForma.boeScenario?.name : 'n/a'}</td>
                                <td>
                                    {formatUTCtoLocal(proForma.updatedAt, 'M/d/yy h:mm a')}
                                    {proForma.updatedBy && (
                                        <span>
                                            {' by '}
                                            {isAllDealsProFormasPage
                                                ? proForma.updatedBy
                                                : <UserDisplay correlationId={proForma.updatedBy} />}
                                        </span>
                                    )}
                                </td>
                                <td>{formatUTCtoLocal(proForma.createdAt, 'M/d/yy')}</td>
                                <td>
                                    {!isAllDealsProFormasPage && proForma.createdBy
                                        ? <UserDisplay correlationId={proForma.createdBy} />
                                        : proForma.createdByName}
                                </td>
                                <td className="d-flex justify-content-between align-items-center">
                                    <NSBadge color={PRO_FORMA_STATUS_MAPPING[proForma.proFormaStatusId].color}>
                                        {PRO_FORMA_STATUS_MAPPING[proForma.proFormaStatusId].name}
                                    </NSBadge>
                                    <RenderIf isTrue={!isAllDealsProFormasPage}>
                                        <UncontrolledDropdown>
                                            <DropdownToggle tag="div">
                                                <NSIcon icon={IconDotsVertical} className="cursor--pointer" role="button" />
                                            </DropdownToggle>
                                            <DropdownMenu container="body" className="p-0 m-0 bg-transparent z-index-100">
                                                <NSCard className="NSCard--level-5 shadow-none">
                                                    {getProFormaEllipsisOptions(
                                                        proForma.proFormaStatusId,
                                                        proForma.isActive,
                                                        proForma.subprojectId != null,
                                                    ).map(option => (
                                                        <DropdownItem
                                                            key={`${option}-${proForma.id}`}
                                                            onClick={() => handleEllipsisOption(option, proForma)}
                                                            className={`text-dark ${getDropdownHeapClassName(option)}`}
                                                        >
                                                            {option}
                                                        </DropdownItem>
                                                    ))}
                                                </NSCard>
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    </RenderIf>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </NSTable>
            </RenderIf>
            <NSModal
                isOpen={showDeleteConfirmationModal}
                toggle={() => setShowDeleteConfirmationModal(false)}
                modalHeaderText="Delete pro forma"
                modalHeaderClassName="text-white bg-danger"
                modalFooterButtonText="Delete"
                modalFooterButtonColor="danger"
                modalFooterCancelButtonText="Cancel"
                modalFooterButtonFunction={handleConfirmDelete}
                modalFooterIsButtonFunctionDisabled={isDeleting}
                modalFooterButtonIsLoading={isDeleting}
            >
                <div className="mb-1">
                    <div className="text-white">
                        {proFormaToDelete?.isLinkedToProject
                            ? DELETE_LINKED_PRO_FORMA_CONFIRMATION_TEXT
                            : DELETE_PRO_FORMA_CONFIRMATION_TEXT}
                    </div>
                </div>
            </NSModal>
            {proFormaToLink && (
                <LinkToProjectModal
                    isOpen
                    toggle={() => setProFormaToLink(null)}
                    dealId={Number(dealId)}
                    proFormaId={proFormaToLink.id}
                    proFormaCorrelationId={proFormaToLink.correlationId}
                    assetTypeCorrelationId={proFormaToLink.assetTypeCorrelationId}
                    assetTypeName={formatProFormaAssetTypeName(proFormaToLink.proFormaModelName)}
                />
            )}
            {proFormaToUnlink && (
                <UnlinkProjectModal
                    isOpen
                    toggle={() => setProFormaToUnlink(null)}
                    dealId={Number(dealId)}
                    proFormaId={proFormaToUnlink.id}
                />
            )}
        </>
    );
};

export default DealAutomatedTable;
